/* Bastant LP — behaviour for the bastant-lp-* sections: gallery, finish swatches, add to cart, bundle nudge, back-in-stock. */
(() => {
  if (window.BastantLP) return;

  const formatKr = (cents) => {
    const value = Number(cents) || 0;
    const kr = Math.floor(value / 100);
    const ore = value % 100;
    const whole = String(kr).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return `${whole}${ore ? ',' + String(ore).padStart(2, '0') : ''} kr.`;
  };

  const fill = (template, tokens) =>
    String(template || '').replace(/\[([a-z_]+)\]/g, (match, key) => (key in tokens ? tokens[key] : match));

  const routes = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  /* Adds items through the Ajax cart API and fires the same events as Impact's own product form,
     so the theme's cart drawer and cart count update exactly as they do elsewhere on the site. */
  async function addToCart(items, source) {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } })
    );

    const response = await fetch(`${routes()}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items, sections: sections.join(',') }),
    });
    const json = await response.json();

    if (!response.ok) {
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      throw new Error(json.description || json.message || 'Varen kunne ikke lægges i kurven.');
    }

    const settings = (window.themeVariables && window.themeVariables.settings) || {};
    if (settings.cartType === 'page') {
      window.location.href = `${routes()}cart`;
      return;
    }

    const cart = await (await fetch(`${routes()}cart.js`)).json();
    cart.sections = json.sections;

    (source || document.documentElement).dispatchEvent(
      new CustomEvent('variant:add', {
        bubbles: true,
        detail: { items: json.items || [json], cart },
      })
    );
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart } })
    );
  }

  function setBusy(button, busy) {
    if (!button) return;
    button.setAttribute('aria-busy', busy ? 'true' : 'false');
    button.disabled = busy;
  }

  /* Any <form data-blp-cart> posts its variant (or the bundle variant when a nudge is ticked). */
  function bindCartForm(form) {
    if (form.dataset.blpBound) return;
    form.dataset.blpBound = 'true';

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const button = form.querySelector('[type="submit"]');
      const error = form.querySelector('.blp-error');
      const nudge = form.closest('.blp-buy')?.querySelector('[data-blp-nudge-input]');
      const bundle = Boolean(nudge && nudge.checked);
      const id = bundle ? nudge.value : form.querySelector('[name="id"]').value;
      /* preorder (Bastant portal): the expected ship date goes on the order line, like the product page does */
      const item = { id: Number(id), quantity: 1 };
      if (!bundle && form.dataset.blpPreorderName && form.dataset.blpPreorderValue) {
        item.properties = { [form.dataset.blpPreorderName]: form.dataset.blpPreorderValue };
      }

      if (error) error.textContent = '';
      setBusy(button, true);

      try {
        await addToCart([item], form);
      } catch (err) {
        if (error) error.textContent = err.message;
      } finally {
        setBusy(button, false);
      }
    });
  }

  /* Gallery: thumbnails swap the main image. */
  function bindGallery(gallery) {
    const main = gallery.querySelector('.blp-gallery__main img');
    const thumbs = gallery.querySelectorAll('[data-blp-thumb]');
    if (!main) return;

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        main.src = thumb.dataset.src;
        main.srcset = thumb.dataset.srcset;
        main.alt = thumb.dataset.alt || '';
        thumbs.forEach((t) => t.setAttribute('aria-current', t === thumb ? 'true' : 'false'));
      });
    });
  }

  /* Buy box: finish swatches, stock line, bundle nudge, back-in-stock form. */
  function bindBuyBox(box) {
    const swatches = Array.from(box.querySelectorAll('[data-blp-swatch]'));
    const valueEl = box.querySelector('[data-blp-finish-value]');
    const priceEl = box.querySelector('[data-blp-price]');
    const stockEl = box.querySelector('[data-blp-stock]');
    const cartButtonLabel = box.querySelector('[data-blp-cart-label]');
    const nudgeInput = box.querySelector('[data-blp-nudge-input]');
    const notify = box.querySelector('[data-blp-notify]');
    const notifyIntro = box.querySelector('[data-blp-notify-intro]');
    const notifyLink = box.querySelector('[data-blp-notify-link]');
    const texts = JSON.parse(box.querySelector('[data-blp-texts]')?.textContent || '{}');

    const current = swatches.find((s) => s.dataset.current === 'true');
    const currentState = {
      name: box.dataset.finishName,
      price: Number(box.dataset.price),
      stock: stockEl ? stockEl.dataset.state : 'instock',
      /* server-rendered line for this product (preorder date, "Kun N tilbage", the batch count), put back as is */
      stockHtml: stockEl ? stockEl.innerHTML : '',
      variantId: box.dataset.variantId,
      available: box.dataset.available === 'true',
      url: box.dataset.url,
    };

    const setStock = (state, html) => {
      if (!stockEl) return;
      stockEl.dataset.state = state;
      if (html != null) stockEl.innerHTML = html;
      else stockEl.textContent = texts[`stock_${state}`] || '';
    };

    const updateCartLabel = () => {
      if (!cartButtonLabel) return;
      const bundle = nudgeInput && nudgeInput.checked;
      cartButtonLabel.textContent = bundle ? texts.bundle_button : texts.cart_button;
    };

    const showNotify = (state) => {
      box.dataset.mode = 'notify';
      if (notify) {
        notify.querySelector('[name="variant"]').value = state.variantId;
        notify.querySelector('[data-blp-notify-form]').hidden = false;
        const done = notify.querySelector('[data-blp-notify-done]');
        if (done) done.hidden = true;
      }
      if (notifyIntro) notifyIntro.textContent = fill(texts.notify_intro, { finish: state.name.toLowerCase() });
      if (notifyLink) {
        const isCurrent = state.url === currentState.url;
        notifyLink.hidden = isCurrent;
        notifyLink.href = state.url;
        notifyLink.textContent = fill(texts.notify_link, { finish: state.name.toLowerCase() });
      }
    };

    const select = (swatch) => {
      swatches.forEach((s) => {
        s.setAttribute('aria-checked', s === swatch ? 'true' : 'false');
        s.tabIndex = s === swatch ? 0 : -1;
      });

      const isCurrent = swatch.dataset.current === 'true';
      const state = isCurrent
        ? currentState
        : {
            name: swatch.dataset.name,
            price: Number(swatch.dataset.price),
            variantId: swatch.dataset.variantId,
            available: swatch.dataset.available === 'true',
            url: swatch.dataset.url,
          };

      if (valueEl) valueEl.textContent = state.name;
      if (priceEl) priceEl.textContent = formatKr(state.price);

      if (isCurrent) {
        setStock(currentState.stock, currentState.stockHtml);
        if (currentState.available) {
          box.dataset.mode = 'cart';
        } else {
          showNotify(currentState);
        }
        return;
      }

      setStock('soldout');
      showNotify(state);
    };

    swatches.forEach((swatch, index) => {
      swatch.addEventListener('click', (event) => {
        const isCurrent = swatch.dataset.current === 'true';
        if (!isCurrent) {
          /* Every other finish is a real link to its own product page, sold out or not
             (Gry 28/10: so it can be shared and wish-listed; the page itself shows "Giv mig besked"). */
          return;
        }
        event.preventDefault();
        select(swatch);
      });

      swatch.addEventListener('keydown', (event) => {
        const keys = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
        if (!(event.key in keys)) return;
        event.preventDefault();
        const next = swatches[(index + keys[event.key] + swatches.length) % swatches.length];
        next.focus();
      });
    });

    if (nudgeInput) {
      nudgeInput.addEventListener('change', updateCartLabel);
      updateCartLabel();
    }

    if (current && !currentState.available) showNotify(currentState);

    const notifyForm = box.querySelector('[data-blp-notify-form]');
    if (notifyForm) {
      notifyForm.addEventListener('submit', async (event) => {
        event.preventDefault();
        const button = notifyForm.querySelector('[type="submit"]');
        const error = notify.querySelector('.blp-error');
        const email = notifyForm.querySelector('[name="email"]').value.trim();
        const variant = notifyForm.querySelector('[name="variant"]').value;
        const company = notifyForm.dataset.company;

        error.textContent = '';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          error.textContent = texts.notify_invalid;
          return;
        }

        setBusy(button, true);
        try {
          const response = await fetch(
            `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${encodeURIComponent(company)}`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/vnd.api+json', revision: '2024-10-15' },
              body: JSON.stringify({
                data: {
                  type: 'back-in-stock-subscription',
                  attributes: {
                    profile: { data: { type: 'profile', attributes: { email } } },
                    channels: ['EMAIL'],
                  },
                  relationships: {
                    variant: { data: { type: 'catalog-variant', id: `$shopify:::$default:::${variant}` } },
                  },
                },
              }),
            }
          );
          if (!response.ok) throw new Error(String(response.status));
          notifyForm.hidden = true;
          const done = notify.querySelector('[data-blp-notify-done]');
          if (done) done.hidden = false;
        } catch (err) {
          error.textContent = texts.notify_error;
        } finally {
          setBusy(button, false);
        }
      });
    }
  }

  function init(root) {
    root.querySelectorAll('[data-blp-gallery]').forEach(bindGallery);
    root.querySelectorAll('[data-blp-buy]').forEach(bindBuyBox);
    root.querySelectorAll('form[data-blp-cart]').forEach(bindCartForm);
  }

  window.BastantLP = { init, formatKr };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }

  /* Theme editor: re-bind when a section is re-rendered. */
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
